import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { resolvePath, RouteConfig, useNavigate, useQuery } from '../../routes';
import { ReactComponent as KatalonHeaderIcon } from '../icons/KatalonHeaderIcon.svg';
import {
  AccountSelection,
  DocumentationMenu,
  UserMenu,
} from '.';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    height: theme.spacing(7.5),
    color: theme.palette.getContrastText(theme.palette.primary.main),
    boxShadow: '0px 6px 9px rgba(113, 131, 175, 0.05)',
    justifyContent: 'center',
    padding: '0 0 0 1rem',
    position: 'relative',
    zIndex: theme.zIndex.drawer + 1,
  },
  grid: {
    height: '100%',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  tabSection: {
    color: '#fff !important',
    flexGrow: 1,
    '& .MuiTabs-flexContainer': {
      height: '100%',
      justifyContent: 'flex-start',
    },
  },
  tabs: {
    height: theme.spacing(7.5),
  },
  tab: {
    margin: theme.spacing(0),
    padding: theme.spacing(0, 3),
    color: theme.palette.grey[100],
    opacity: 0.5,
    '&.Mui-selected': {
      color: theme.palette.getContrastText(theme.palette.primary.main),
    },
  },
  controlSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0 .5rem',
    flexGrow: 1,
    [theme.breakpoints.down('sm')]: {
      width: 0,
    },
  },
  settingSection: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(2),
  },
  testOpsIconWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  brandName: {
    color: '#fff',
    fontWeight: 600,
    fontSize: theme.spacing(3),
    marginLeft: theme.spacing(0.75),
  },
  icon: {
    marginRight: theme.spacing(3.5),
  },
}));

interface NavigationBarProps {
  routes: RouteConfig[];
}

export const NavigationBar = ({ routes }: NavigationBarProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const { pathname } = useLocation();
  const { replace, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();

  const tabs = routes
    .filter(route => route.showInMenu)
    .map(route => ({
      label: intl.formatMessage({ id: route.pageTitle }),
      routeConfig: route,
    }));
  const tabValue = tabs.findIndex(t => pathname.startsWith(t.routeConfig.path));

  const handleTabSelect = (routeConfig: RouteConfig) => {
    replace(resolvePath(routeConfig), replaceQuery(queryDictionary()));
  };

  return (
    <AppBar
      className={classes.root}
      elevation={0}
      sx={{ display: 'flex' }}
    >
      <Grid classes={{ root: classes.grid }} container>
        <Grid item className={classes.logoSection}>
          <KatalonHeaderIcon className={classes.icon} />
          <AccountSelection />
        </Grid>
        <Grid item className={classes.tabSection}>
          <Tabs
            value={tabValue}
            className={classes.tabs}
            TabIndicatorProps={{
              sx: {
                backgroundColor: 'gray',
                height: '3px',
              },
            }}
            textColor="primary"
          >
            {tabs.map(t => (
              <Tab
                key={t.routeConfig.path}
                className={classes.tab}
                onClick={() => handleTabSelect(t.routeConfig)}
                label={t.label}
              />
            ))}
          </Tabs>
        </Grid>
        <Grid item className={classes.controlSection}>
          <div className={classes.settingSection}>
            <DocumentationMenu />
            <UserMenu />
          </div>
        </Grid>
      </Grid>
    </AppBar>
  );
};
